import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import { Modal as UiModal } from "../../../../components/ui/Modal";
import { Button } from "../../../../components/ui/Button";
import { Select } from "../../../../components/ui/Select";
import { getRoles, changeTeamUserRole, rolesErrorMessage } from "../../../../api/roles";
import { InlineError } from "./RoleDialogs";
import { ROLE_KINDS } from "./rolesModel";

/** id del rol propio de un integrante del equipo (la lista puede traerlo como `id` o `_id`). */
export const customRoleIdOf = (member) => member?.customRole?.id ?? member?.customRole?._id ?? "";

/**
 * "Cambiar rol" de un integrante del equipo interno: rol base gestionable +
 * rol propio opcional filtrado por ese base. El backend revalida todo (rango,
 * mismo operador, base del rol propio); acá solo se evita ofrecer combinaciones
 * que sabemos que rebotarían.
 */
export default function ChangeRoleModal({ open, member, currentUserId, onClose, onChanged }) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [baseRoleId, setBaseRoleId] = useState("");
  const [customRoleId, setCustomRoleId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const memberId = member?._id;
  const isSelf = !!memberId && memberId === currentUserId;

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setError("");
    setSaving(false);
    setBaseRoleId(member?.role?._id || "");
    setCustomRoleId(customRoleIdOf(member));
    setLoading(true);
    getRoles().then(({ data, error: err }) => {
      if (!alive) return;
      setLoading(false);
      if (err) {
        const tr = tRef.current;
        setError(rolesErrorMessage(err, tr, tr("No se pudieron cargar los roles. Intenta de nuevo.")));
        return;
      }
      setRoles(Array.isArray(data?.roles) ? data.roles : []);
    });
    return () => { alive = false; };
    // Se recarga al abrir para ver el estado actual de los roles; `member` entra por su id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memberId]);

  const baseOptions = roles.filter((r) => r.kind !== ROLE_KINDS.CUSTOM && r.assignable);
  const customOptions = roles.filter(
    (r) => r.kind === ROLE_KINDS.CUSTOM && r.assignable && r.baseRoleId === baseRoleId,
  );

  const onBaseChange = (value) => {
    setBaseRoleId(value);
    setCustomRoleId("");
  };

  const unchanged = baseRoleId === (member?.role?._id || "") && customRoleId === customRoleIdOf(member);
  const canSubmit = !!baseRoleId && !isSelf && !unchanged && !loading;

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError("");
    const { data, error: err } = await changeTeamUserRole(memberId, {
      baseRoleId,
      customRoleId: customRoleId || null,
    });
    setSaving(false);
    if (err) {
      setError(rolesErrorMessage(err, t, t("No se pudo cambiar el rol. Intenta de nuevo.")));
      return;
    }
    toast.success(t("Rol actualizado"));
    onChanged(memberId, data);
  };

  return (
    <UiModal
      open={open}
      onClose={() => !saving && onClose()}
      size="md"
      title={t("Cambiar rol")}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t("Cancelar")}</Button>
          <Button onClick={submit} loading={saving} disabled={!canSubmit}>{t("Cambiar rol")}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-fg-muted">
          {t("Usuario: {{name}}", { name: `${member?.name || ""} ${member?.lastName || ""}`.trim() || member?.email || "" })}
        </p>
        {isSelf && (
          <p role="note" className="text-sm text-status-warning">{t("No puedes cambiar tu propio rol.")}</p>
        )}
        <Select
          label={t("Rol base")}
          value={baseRoleId}
          disabled={loading || isSelf}
          onChange={(e) => onBaseChange(e.target.value)}
          placeholder={t("Selecciona un rol base")}
          options={baseOptions.map((r) => ({ value: r.baseRoleId, label: r.name }))}
        />
        <Select
          label={t("Rol propio (opcional)")}
          value={customRoleId}
          disabled={loading || isSelf || !baseRoleId}
          onChange={(e) => setCustomRoleId(e.target.value)}
          options={[
            { value: "", label: t("Sin rol propio (usar el rol base)") },
            ...customOptions.map((r) => ({ value: r.id, label: r.name })),
          ]}
        />
        <p className="text-xs text-fg-muted">
          {t("Los cambios se aplican a cada usuario la próxima vez que se renueve su sesión (máximo 1 hora).")}
        </p>
        <InlineError>{error}</InlineError>
      </div>
    </UiModal>
  );
}
